import { client } from './claims';

/**
 * CAF Classifier Corpus — admin API helper.
 *
 * Backend routes live under `/api/admin/caf-corpus`.
 * Auth is supplied by the shared axios `client` interceptor (Bearer token
 * from localStorage), matching the pattern used by `backfill.ts` etc.
 */

export type CafCorpusAction =
  | 'AUTO_ARCHIVE'
  | 'AUTO_ASSIGN_COMPLETE'
  | 'AUTO_ASSIGN_ONLY'
  | 'AUTO_ASSIGN_SEND_LOA'
  | 'AUTO_ASSIGN_MULTI_REP'
  | 'AUTO_ASSIGN_CANCEL_CONFIRM'
  | 'MANUAL_REVIEW';

/** Human-readable labels for the classifier actions. */
export const CAF_ACTION_LABELS: Record<CafCorpusAction, string> = {
  AUTO_ARCHIVE: 'Auto-archive',
  AUTO_ASSIGN_COMPLETE: 'Auto-assign and complete',
  AUTO_ASSIGN_ONLY: 'Auto-assign only',
  AUTO_ASSIGN_SEND_LOA: 'Auto-assign & send LOA',
  AUTO_ASSIGN_MULTI_REP: 'Auto-assign & send multi-rep',
  AUTO_ASSIGN_CANCEL_CONFIRM: 'Attach and reaffirm authority',
  MANUAL_REVIEW: 'Manual review',
};

/** Spec §4.3: whether an approved example's emails are final responses. Required on every save. */
export type CafFinalResponseStance = 'final_response' | 'not_final_response';
export const CAF_FINAL_RESPONSE_STANCES: CafFinalResponseStance[] = ['final_response', 'not_final_response'];

export const CAF_ACTION_VALUES: CafCorpusAction[] = [
  'AUTO_ARCHIVE',
  'AUTO_ASSIGN_COMPLETE',
  'AUTO_ASSIGN_ONLY',
  'AUTO_ASSIGN_SEND_LOA',
  'AUTO_ASSIGN_MULTI_REP',
  'AUTO_ASSIGN_CANCEL_CONFIRM',
  'MANUAL_REVIEW',
];

/** Task 5: an OPTIONAL classification a curator may attach alongside the
 *  action on a corpus entry (independent of `CafCorpusAction`). Mirrors
 *  `caf_corpus_service.ALLOWED_CATEGORY_OVERRIDES` / the three literal
 *  strings in `caf_task_classifier.INVESTIGATION_CATEGORIES` verbatim --
 *  those strings ALSO become the claim's `craClassificationStatus`, do not
 *  edit them. Task 6 reads `category_override` off precedent hits. */
export type CafCategoryOverride =
  | 'Investigating Fixed Commission'
  | 'Investigating DCA Commission'
  | 'No Investigation as No Commission';

/** Human-readable labels for the optional classification. Identical to the
 *  literal values today, but kept as a lookup so a future re-wording
 *  doesn't have to touch every call site. */
export const CAF_CATEGORY_OVERRIDE_LABELS: Record<CafCategoryOverride, string> = {
  'Investigating Fixed Commission': 'Investigating Fixed Commission',
  'Investigating DCA Commission': 'Investigating DCA Commission',
  'No Investigation as No Commission': 'No Investigation as No Commission',
};

export const CAF_CATEGORY_OVERRIDE_VALUES: CafCategoryOverride[] = [
  'Investigating Fixed Commission',
  'Investigating DCA Commission',
  'No Investigation as No Commission',
];

export type CafClusterStatus = 'unlabelled' | 'labelled';

/* ------------------------------------------------------------------ */
/* Extraction schema (F1a) — data points to pull from matching emails */
/* and map onto claim fields. Storage + authoring only; extraction and  */
/* claim-writes are later tasks.                                         */
/* ------------------------------------------------------------------ */

export type CafExtractionDataType = 'string' | 'number' | 'date' | 'enum';

/** One claim field staff may map an extracted value into (the picklist
 *  served by `GET /caf-corpus/claim-fields`). */
export interface ClaimFieldOption {
  field: string;
  label: string;
  dataType: CafExtractionDataType;
  /** Present only for `dataType === 'enum'`. */
  options?: string[];
}

export interface CafExtractionField {
  /** Slug-like, unique within the schema. */
  id: string;
  label: string;
  description?: string;
  dataType: CafExtractionDataType;
  /** Must be one of the allowlisted claim fields. */
  targetClaimField: string;
  required: boolean;
  /** Present only for `dataType === 'enum'`. */
  options?: string[];
}

export interface CafExtractionSchema {
  enabled: boolean;
  fields: CafExtractionField[];
}

/**
 * An email attachment with a freshly-minted 24h download SAS URL.
 * `download_url` is `null` when the server couldn't mint one (blob missing,
 * not stored, or SAS error) — render the name greyed-out / non-clickable.
 * The stored `secureUrl` (7-day SAS) is deliberately never returned.
 */
export interface CafAttachment {
  id: string;
  name: string;
  contentType: string | null;
  size: number | null;
  download_url: string | null;
}

export interface CafClusterSample {
  id: string;
  sender: string;
  subject: string | null;
  received_date: string | null;
  /** Whether this id is still in the active email_tasks table. */
  active: boolean;
  /** Persisted classifier/curator final-response flag. */
  has_final_response: boolean;
  final_response_value_known?: boolean;
  final_response_provenance?: 'ai' | 'staff' | 'legacy_unknown';
  final_response_actor?: string;
  final_response_changed_at?: string;
  final_response_source_hash?: string;
  final_response_needs_review?: boolean;
  /** Capped server-side -- 4000 chars for cluster drawer samples/members,
   *  20000 chars for the Create Cluster picker's single-email preview (see
   *  `getEmailPreview`, ticket #379). `null` if SQL enrichment failed. */
  body?: string | null;
  /** Attachments with fresh download SAS URLs; empty if none / enrichment failed. */
  attachments?: CafAttachment[];
  /** Discovery failed; an empty list must not be described as no attachments. */
  attachments_unavailable?: boolean;
  /** True when the server cut `body` at its cap. Absent on older/soft-failed
   *  responses -- treat as falsy. */
  truncated?: boolean;
}

export interface CafClusterListItem {
  id: string;
  sender: string;
  member_count: number;
  sample_task_ids: string[];
  status: CafClusterStatus;
  labelled_corpus_entry_id: string | null;
  labelled_by: string | null;
  labelled_at: string | null;
  created_at: string;
  last_updated_at: string;
  /** Teach the AI queue: up to two subjects, present only with `with_subjects=1`. */
  sample_subjects?: string[];
  /** #705, Teach the AI queue only: the switched-on approved example from the same sender this
   *  group is already as close to as the AI needs to use it. Absent when there is none. */
  looks_like?: CafLooksLike;
}

export interface CafLooksLike {
  entry_id: string;
  sender: string;
  action: string;
  final_response_stance?: CafFinalResponseStance | null;
  note: string;
  similarity: number;
}

/** Read-only view of the corpus entry a cluster label would replace (navigation
 *  Task 7 I1): a pre-cutover label whose cluster link was never written. The
 *  curator reviews it and sends its `version` (and `compare_token` at version 0)
 *  as the label's CAS. Absent on an older backend; null when no entry exists. */
export interface CafClusterLabelEntry {
  id: string;
  version: number;
  compare_token: string | null;
  active: boolean | null;
  correct_action: CafCorpusAction | string | null;
  note: string | null;
  final_response_stance?: CafFinalResponseStance | null;
}

export interface CafClusterSplitRequest {
  task_ids: string[];
  expected_etag: string;
  operation_id: string;
  reason: string;
}

export interface CafClusterDetail extends CafClusterListItem {
  _etag?: string;
  membership_label_reserved?: boolean;
  split_pending?: { request: CafClusterSplitRequest } | null;
  label_entry?: CafClusterLabelEntry | null;
  member_task_ids: string[];
  /** Don't render — large float array. */
  centroid_embedding?: number[];
  samples: CafClusterSample[];
}

/** One embedded email task for a sender, as returned by the embeddings list
 *  endpoint (the row picker in the manual-clustering tab). */
export interface CafEmbeddingItem {
  id: string;
  sender: string;
  subject: string | null;
  received_date: string | null;
}

export interface CafCorpusEntry {
  id: string;
  email_task_id: string;
  sender: string;
  subject_snapshot: string | null;
  correct_action: CafCorpusAction;
  note: string | null;
  labelled_by: string | null;
  labelled_at: string | null;
  source: 'live_override' | 'cluster_label';
  cluster_id: string | null;
  active: boolean;
  amended_by?: string | null;
  amended_at?: string | null;
  /** Optional extraction schema authored by staff. `null`/absent = none. */
  extraction_schema?: CafExtractionSchema | null;
  /** Ticket #381 (Ryan's 2026-07-15 decision): curator-owned per-sender
   *  archive-gate bypass. Only meaningful when `correct_action ===
   *  'AUTO_ARCHIVE'`. Absent/false on entries that predate this feature. */
  archive_override_refs?: boolean;
  /** Task 5: optional curator classification attached alongside the
   *  action. Absent when not set. */
  category_override?: CafCategoryOverride | null;
  /** Task 5 optimistic-concurrency identity. Legacy entries report version 0. */
  version?: number;
  /** Opaque reviewed-content token, required for the first legacy amendment. */
  compare_token?: string;
  reason?: string | null;
  guidance_committed?: boolean;
  retrieval_ready?: boolean;
  approved_active?: boolean;
  approval_reason?: string;
  searchable_vector_ready?: boolean;
  vector_status?: string;
  projection_pending?: Record<string, unknown> | null;
  publication_status?: 'complete' | 'partial';
  projection_status?: 'pending' | 'complete' | 'superseded';
  /** Teach the AI: linked group size (1 for a single-email example); present with `sizes=1`. */
  group_size?: number | null;
  deleted_at?: string | null;
  reactivated_at?: string | null;
  /** Spec §4.3. Absent on examples saved before it existed. */
  final_response_stance?: CafFinalResponseStance | null;
  /** Ruling R7: conflict warnings staff saw and saved past, on this version. */
  save_warnings?: string[];
  /** #705, the edit reply only: the example's workflow link after the save checked it against what
   *  the example now says (`changed` when the save moved it). Absent when there is no link row. */
  workflow_link?: { workflow_id: string | null; revision: number; changed: boolean };
}

export interface CursorPage<T> {
  items: T[];
  next_cursor: string | null;
}

/** Offset-paged response shape, e.g. the corpus-entries list (ticket #398 --
 *  switched off Cosmos continuation-token paging, which 500'd once the raw
 *  cross-partition ORDER BY token got round-tripped through a GET query
 *  param). No `total` -- `next_offset` is inferred server-side from page
 *  fullness rather than a separate COUNT query. */
export interface OffsetPage<T> {
  items: T[];
  next_offset: number | null;
}

/** Offset-paged response of the cluster all-members endpoint. Unlike the
 *  cursor-based lists elsewhere, `member_task_ids` is a plain list embedded
 *  on the cluster doc, so integer offsets are simpler and stabler here. */
export interface CafClusterMembersPage {
  items: CafClusterSample[];
  total: number;
  next_offset: number | null;
}

export interface ClusterListQuery {
  status?: CafClusterStatus;
  limit?: number;
  cursor?: string | null;
}

export interface EntryListQuery {
  sender?: string;
  action?: CafCorpusAction;
  limit?: number;
  /** Offset-paged (ticket #398) -- see `OffsetPage`. */
  offset?: number;
}

export interface LabelClusterPayload {
  expected_cluster_etag?: string;
  action: CafCorpusAction;
  note: string;
  /** Omitted on a backend without the setting (final fix wave C1/C2, components/curation/finalResponseSupport). */
  final_response_stance?: CafFinalResponseStance;
  acknowledged_warnings?: string[];
  extraction_schema?: CafExtractionSchema | null;
  /** Ticket #381 -- only meaningful when `action === 'AUTO_ARCHIVE'`. */
  archive_override_refs?: boolean;
  /** Task 5 -- optional curator classification, sent alongside the action. */
  categoryOverride?: CafCategoryOverride;
  expected_version: number;
  reason: string;
  reviewed_token?: string;
}

export interface FromTaskPayload {
  task_id: string;
  action: CafCorpusAction;
  note: string;
  /** Omitted on a backend without the setting (final fix wave C1/C2, components/curation/finalResponseSupport). */
  final_response_stance?: CafFinalResponseStance;
  acknowledged_warnings?: string[];
  extraction_schema?: CafExtractionSchema | null;
  /** Task 5 -- optional curator classification, sent alongside the action. */
  categoryOverride?: CafCategoryOverride;
  expected_version: number;
  reason: string;
  reviewed_token?: string;
}

export interface MarkFinalResponseOutcome {
  success: boolean;
  message: string;
  task_id: string;
  already_marked: boolean;
  timeline_recorded: boolean;
}

/** Task 6b / Ryan ruling: Mark final response gets a real Undo, not a
 *  confirm-once dialog. `has_final_response` is whether the flag is set after
 *  the restore -- always a boolean: a restored NULL (never evaluated) reads as
 *  `false` here, though the column itself goes back to NULL. A repeat undo is a
 *  no-op success (`already_undone: true`). */
export interface UnmarkFinalResponseOutcome {
  success: boolean;
  message: string;
  task_id: string;
  already_undone: boolean;
  has_final_response: boolean;
  timeline_recorded: boolean;
}

/** 409 refusal codes: the flag was not this action's live mark to remove.
 *  `not_marked` -- no mark, or a detector/backfill/reviewed-correction flag;
 *  `marked_before_undo` -- a legacy mark with no recorded prior value;
 *  `mark_changed` -- the mark (or the flag it set) has changed since. */
export type CafUnmarkFinalResponseErrorCode = 'not_marked' | 'marked_before_undo' | 'mark_changed';

export interface UpdateEntryPayload {
  action?: CafCorpusAction;
  note?: string;
  /** Omitted on a backend without the setting (final fix wave C1/C2, components/curation/finalResponseSupport). */
  final_response_stance?: CafFinalResponseStance;
  acknowledged_warnings?: string[];
  /** Present to amend the schema; `null` clears it; omit to leave as-is. */
  extraction_schema?: CafExtractionSchema | null;
  /** Ticket #381 -- present to amend the bypass flag; omit to leave as-is.
   *  Only meaningful when the entry's action (after this PATCH) is
   *  'AUTO_ARCHIVE'. */
  archive_override_refs?: boolean;
  /** Task 5 -- present to amend the classification; omit to leave as-is.
   *  `null` explicitly clears a previously-set value. */
  categoryOverride?: CafCategoryOverride | null;
  expected_version: number;
  reason: string;
  reviewed_token?: string;
}

export interface SenderEmbeddingsQuery {
  limit?: number;
  cursor?: string | null;
  /** Case-insensitive CONTAINS filter on subject (optional). */
  subject?: string;
  /** Hide emails already clustered or actioned (completed/archived).
   *  Route defaults this to `true` server-side when omitted; the UI sends
   *  it explicitly so unchecking the "hide" box actually shows everything. */
  excludeActioned?: boolean;
}

/** Payload for hand-forming a cluster from picked task ids. When BOTH
 *  `action` and `note` are present the backend labels the cluster in the
 *  same call (the "label now" path). */
export interface ManualClusterPayload {
  sender: string;
  task_ids: string[];
  name?: string;
  action?: CafCorpusAction;
  note?: string;
  final_response_stance?: CafFinalResponseStance;
  acknowledged_warnings?: string[];
  extraction_schema?: CafExtractionSchema | null;
  /** Ticket #381 -- only meaningful on the one-step label path (action +
   *  note both present), with action === 'AUTO_ARCHIVE'. */
  archive_override_refs?: boolean;
  /** Task 5 -- optional curator classification; only meaningful on the
   *  one-step label path (action + note both present). */
  categoryOverride?: CafCategoryOverride;
  /** Required when action + note publish guidance in the same request. */
  expected_version?: number;
  /** Required when action + note publish guidance in the same request. */
  reason?: string;
  reviewed_token?: string;
}

/** Response of the manual-cluster create endpoint. `entry` is only present
 *  when the cluster was labelled in the same call. */
export interface ManualClusterResult {
  cluster: CafClusterListItem & Partial<CafClusterDetail>;
  entry?: CafCorpusEntry;
}

/* ------------------------------------------------------------------ */
/* AI Performance tab (read-only stats + decision feed)                 */
/* Shapes mirror python-backend/app/services/caf_performance_service.py */
/* (get_performance_stats / list_decisions return dicts).               */
/* ------------------------------------------------------------------ */

/** One day in the performance window (service `daily` list). */
export interface CafDailyStat {
  /** YYYY-MM-DD */
  date: string;
  classified: number;
  /** Counts keyed by ai_action; may include 'UNKNOWN' for null actions. */
  by_action: Record<string, number>;
  completed_by_processor: number;
}

export interface CafPerformanceHeadlines {
  classified_total: number;
  /** Percentage 0–100, 1dp. */
  auto_handled_pct: number;
  /** Percentage 0–100, 1dp — classifications with >=1 precedent. */
  precedent_hit_rate: number;
  processor_completed_total: number;
  /** `null` when nothing completed in the window — render a dash. */
  avg_completion_minutes: number | null;
  median_completion_minutes: number | null;
  avg_confidence_by_action: Record<string, number>;
  low_confidence_count: number;
  override_count: number;
}

/** A top-used precedent. When the corpus entry no longer resolves the
 *  backend returns only `{id, uses, missing: true}`. */
export interface CafTopPrecedent {
  id: string;
  uses: number;
  sender?: string | null;
  correct_action?: string | null;
  note?: string | null;
  missing?: boolean;
}

export interface CafCorpusHealth {
  active_entries: number;
  entries_by_action: Record<string, number>;
  unlabelled_clusters: number;
  top_precedents: CafTopPrecedent[];
  /** Window-relative: active entries not used in the last N days. */
  never_used_in_window_count: number;
  never_used_in_window_sample: string[];
}

export interface CafPerformanceStats {
  window_days: number;
  /** ISO datetime the window opened. */
  since: string;
  daily: CafDailyStat[];
  headlines: CafPerformanceHeadlines;
  corpus: CafCorpusHealth;
}

/** A precedent used by one decision. `missing: true` (id only) when the
 *  corpus entry could not be resolved (deleted/purged). */
export interface CafDecisionPrecedent {
  id: string;
  sender?: string | null;
  correct_action?: string | null;
  note?: string | null;
  missing?: boolean;
}

/** Known server-computed review flags. The feed may grow new flag strings,
 *  so render unknown values as plain chips — never key rendering on this
 *  union alone. */
export type CafDecisionFlag =
  | 'low_confidence'
  | 'no_precedent_auto'
  | 'precedent_disagreement'
  | 'cancelled_claim_housekeeping'
  | 'corrected';

/** One claim-field write recorded by the extraction audit
 *  (`caf_backlog_processor_service._apply_and_replace` change dicts). */
export interface CafFieldWriteChange {
  field: string;
  sourceFieldId?: string | null;
  oldValue?: unknown;
  newValue?: unknown;
}

/** Parsed `ai_fields_written` audit payload. Canonical shape (see
 *  `_store_extraction_audit`) is `{written: [...], dropped: {field: reason}}`,
 *  but the column is raw JSON — render defensively (legacy rows may carry a
 *  bare change array or other JSON; `_safe_fields_written` server-side only
 *  guarantees object-or-array-or-null). */
export interface CafFieldsWritten {
  written?: CafFieldWriteChange[];
  dropped?: Record<string, string>;
}

export type CafStaffCheck = 'correct' | 'incorrect' | 'unsure';

export interface CafDecisionRow {
  id: string;
  email_from: string | null;
  subject: string | null;
  processed_at: string | null;
  category: string | null;
  /** Usually one of CafCorpusAction, but typed loosely — old/null rows. */
  action: string | null;
  confidence: number | null;
  reasoning: string | null;
  claim_reference: string | null;
  assigned_claim_id: string | null;
  completed: boolean;
  completed_by: string | null;
  /** Staff member who overrode the AI's decision; null when not corrected. */
  overridden_by?: string | null;
  /** ISO datetime of the staff override; null when not corrected. */
  overridden_at?: string | null;
  /** Extraction audit (`ai_fields_written` JSON) — object/array or null. */
  fields_written?: CafFieldsWritten | CafFieldWriteChange[] | null;
  precedents: CafDecisionPrecedent[];
  flags: string[];
  /** True for rows served from archived_email_tasks. */
  archived?: boolean;
  /** Present only when the page was requested with `enrich` and `enriched` is true. */
  decision_id?: string | null;
  hold_reason?: string | null;
  staff_check?: CafStaffCheck | null;
}

/** Result of POST /entries/<id>/requeue — mirrors
 *  `caf_corpus_service.requeue_decisions_for_entry`'s summary dict. */
export interface CafRequeueSummary {
  entry_id: string;
  /** Candidate tasks that referenced this precedent (after safety filters). */
  matched: number;
  /** Rows actually wiped + re-pended. */
  requeued: number;
  /** Human-completed tasks left alone. */
  skipped_staff_touched: number;
}

/** Email body for one decision row, fetched on demand (the feed never
 *  carries bodies). Mirrors `get_decision_body` in the perf service. */
export interface CafDecisionBody {
  id: string;
  /** UNTRUSTED email content — render only via the sandboxed-iframe
   *  `EmailBodyView`. `''` when the task has no body. */
  body: string;
  email_from: string | null;
  subject: string | null;
  /** True when the server cut the body at its 20,000-char cap. */
  truncated: boolean;
}

export interface DecisionListQuery {
  days?: number;
  action?: CafCorpusAction;
  flagged?: boolean;
  limit?: number;
  cursor?: string | null;
  enrich?: boolean;
}

export interface CafDecisionPage extends CursorPage<CafDecisionRow> {
  /** true: every row carries decision_id and staff_check; false or absent: extras unavailable. */
  enriched?: boolean;
  /** true: rows also carry hold_reason (the backend migration has run). */
  reasons?: boolean;
}

export interface CafReplacementItem {
  decision_id: string;
  task_id: string;
  decided_at: string;
  outcome: 'workflow' | 'manual';
  label: 'Would run' | 'Needs a person';
  reason_status: 'ready' | 'unavailable';
  reason_code: string | null;
  workflow_id: string | null;
  proposal: Record<string, unknown>;
  email_from: string | null;
  subject: string | null;
}

export interface CafReplacementReport {
  status: 'ready' | 'not_ready';
  policy_version: string;
  execution_mode: 'watching';
  counting_unit: 'decisions';
  window_days: CafDashboardWindow;
  window_start: string;
  window_end_exclusive: string;
  as_of: string;
  counts: { would_run: number; needs_manual: number; total: number } | null;
  daily: Array<{ date: string; would_run: number; needs_manual: number }> | null;
  reasons_status: 'ready' | 'partial' | 'unavailable';
  manual_reasons: Array<{ code: string; count: number }> | null;
  items: CafReplacementItem[] | null;
  next_cursor: string | null;
  cohort?: string;
}

export interface CafReplacementReportQuery {
  days: CafDashboardWindow;
  outcome?: 'workflow' | 'manual';
  reason?: string;
  cursor?: string;
  cohort?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Shared curator snapshot + immutable decision evidence (Tasks 1/10) */
/* ------------------------------------------------------------------ */

export type CafDashboardWindow = 7 | 14 | 30;
export type CafDashboardStatus = 'ready' | 'not_ready';

export interface CafCountPair {
  events: number;
  distinct_tasks: number;
}

export interface CafAccuracyCohort {
  correct: number;
  incorrect: number;
  unsure: number;
  unreviewed: number;
  /** Correct / (correct + incorrect), or null when there is no denominator. */
  accuracy: number | null;
  reviewed: number;
  eligible_cohort: number;
  assignments?: number;
  completed?: number;
  nonresponse?: number;
  cohort_basis: string;
}

export interface CafDashboardAccuracy extends Omit<CafAccuracyCohort, 'cohort_basis'> {
  random_audit: CafAccuracyCohort;
  targeted_audit: CafAccuracyCohort;
  volunteered: CafAccuracyCohort;
  review_activity: number;
  review_activity_by_source: Record<string, number>;
  verdict_target: string;
  classification_accuracy: number | null;
  execution_accuracy: number | null;
  withdrawal_supported: boolean;
  time_basis: string;
}

export interface CafDashboardSourceProgress {
  completed_at: string | null;
  updated_at: string | null;
  cycles: number;
  cycle_in_progress: boolean;
}

export interface CafDashboardMeasurement {
  as_of: string;
  window_start: string;
  window_end_exclusive: string;
  accuracy: CafDashboardAccuracy;
  scope: string;
  pre_trace_failures: string;
  retry_purpose_unavailable: number;
  measurement_started_at: string | null;
  writer_barrier_at: string | null;
  delivery: string;
  pending_inbox: number;
  oldest_pending_at: string | null;
  projection_lag_seconds: number;
  legacy_completeness: string;
  sources: Record<string, CafDashboardSourceProgress>;
  coverage_completed_at: string | null;
}

export interface CafDashboardBacklog {
  tasks: number;
  age_bands: Array<{ lane: string; age_band: string; tasks: number }>;
  oldest_age_seconds: number | null;
  eligibility: string;
  ambiguous_or_missing: number;
}

export interface CafDailyEffects {
  /** YYYY-MM-DD (UTC day). */
  date: string;
  completed: number;
  archived: number;
  held: number;
  /** Attached to a claim and left open for a person. */
  assigned?: number;
}

export interface CafDashboardThroughput {
  attempts: number;
  distinct_tasks: number;
  effects: Record<string, CafCountPair>;
  intended_actions: Record<string, number>;
  provider_attempts: Record<string, number>;
  model_attempts: Record<string, number>;
  policy_attempts: Record<string, number>;
  explicit_attempt_purpose: Record<string, number>;
  outcome_time_basis: string;
  attempt_time_basis: string;
  backlog: CafDashboardBacklog;
  intake: number;
  /** Teach the AI line chart; absent on snapshots built before the backend change. */
  daily?: CafDailyEffects[];
  /** Distinct staff-bound tasks per hold reason; absent until the migration has run. */
  hold_reasons?: Record<string, number>;
}

export interface CafDashboardRetrieval {
  status_counts: Record<string, number>;
  attempted: number;
  not_attempted: number;
  unrecorded: number;
  readable: number;
  accepted_but_unsupplied: number;
  supplied_decisions: number;
  provider_request_count: number | null;
  duration_ms: {
    mean: number | null;
    max: number | null;
    observations: number;
    p50: number | null;
    p95: number | null;
  };
  request_charge: {
    observed_total: number | null;
    observations: number;
    unobserved: number;
  };
}

export interface CafCoverageSenderGroup {
  sender: string | null;
  lender_key: string | null;
  incoming: number;
  approved_active: number;
  searchable_examples: number;
  existing_examples?: number;
  approval_pending_examples?: number;
  switched_off_examples?: number;
  approved_missing_vector?: number;
}

export interface CafCoverageRefreshStatus {
  retry_at: string | null;
  retry_after_seconds: number | null;
  throttle_count: number | null;
  failed_pages: number | null;
  last_status_code: number | null;
  last_error_code: string | null;
  request_charge: number | null;
  deferred: boolean;
}

export interface CafDashboardCoverage {
  lender_groups?: Array<{ lender_key: string; lender_label: string; incoming: number; approved_active: number; searchable_examples: number }>;
  ambiguous_lender_tasks?: number;
  incoming_lender_groups?: number;
  covered_incoming_lender_groups?: number;
  configured_lender_groups?: number;
  lender_groups_limit?: number;
  zero_example_lender_groups?: number;
  lender_mapping?: { source_id: string; source_etag?: string; captured_at: string; invalid_rules?: number };
  status: 'ready' | 'not_ready';
  lender_identity: string;
  unknown_lender_tasks: number;
  incoming_tasks: number;
  sender_groups: CafCoverageSenderGroup[];
  sender_groups_limit: number;
  sender_is_not_lender: boolean;
  zero_example_sender_groups: number | null;
  archive_scope_unknown_tasks: number;
  unknown_sender_incoming_tasks: number;
  approved_active_examples?: number;
  approval_unverified_examples?: number;
  searchable_examples?: number;
  approved_missing_vector?: number;
  direct_searchable_examples?: number;
  projection_work?: Record<string, number>;
  consistency?: string;
  scan_metrics: Record<string, unknown> | null;
  refresh_status: CafCoverageRefreshStatus;
  last_completed_at: string | null;
  stale: boolean;
}

export interface CafDashboardPrecedents {
  creation_measurement?: CafCreationMeasurement;
  popular_versions: Array<{ entry_id: string; version: string | number; uses: number }>;
  popularity_basis: string;
  supplied_without_historical_cluster: number;
  creation_cadence: number | null;
  creation_cadence_reason: string;
}

export interface CafAwaitingCluster {
  cluster_id: string | null;
  sender: string | null;
  owner: string;
  created_at: string | null;
  memberships: number | null;
  [key: string]: unknown;
}

export interface CafCreationMeasurement {
  status: string;
  window_complete: boolean;
  capture_started_at: string | null;
  writer_barrier_at: string | null;
  coverage_through: string | null;
}

export interface CafDashboardClustering {
  creation_measurement?: CafCreationMeasurement;
  status: 'ready' | 'not_ready';
  awaiting: number | null;
  oldest_age_seconds: number | null;
  awaiting_by_owner?: Record<string, number>;
  awaiting_clusters?: CafAwaitingCluster[];
  represented_memberships?: number;
  distinct_represented_emails?: number | null;
  current_documents_created_in_window?: number;
  new_distinct_groups: number | null;
  creation_cadence_basis?: string;
}

export interface CafSchedulerProjection {
  observed_at: string | null;
  authority?: string;
  status?: string;
  schema_version?: number;
  processor_id?: string;
  configured?: boolean;
  activation_required?: boolean;
  enabled?: boolean;
  persisted_intent?: string;
  interval_seconds?: number;
  configuration_version?: number;
  requested_by?: string | null;
  configuration_updated_at?: string | null;
  next_due_at?: string | null;
  last_scheduled_slot?: string | null;
  active_run?: Record<string, unknown> | null;
  heartbeat_at?: string | null;
  last_outcome?: Record<string, unknown> | null;
  retry_exhausted?: boolean;
  checkpoint?: Record<string, unknown> | null;
  message?: string | null;
}

export interface CafDashboardOperations {
  recorded_events: Record<string, number>;
  outcome_statuses: Record<string, number>;
  errors_and_holds: Record<string, number>;
  model_duration_ms: number | null;
  created_to_completed_duration_ms: number | null;
  embedding: Record<string, unknown>;
  senders: Record<string, unknown>;
  outbound: Record<string, unknown>;
  memory_accounting: string;
  scheduler: Record<string, CafSchedulerProjection>;
  legacy_error_code: string | null;
  coverage_error_code: string | null;
  coverage_failed_page_observed_charge: number | null;
  build_duration_seconds: number;
}

export interface CafDashboardBootstrap {
  phase: 'coverage' | 'scanning_tasks' | 'scanning_history' | 'finalizing';
  completed_initial_scans: number;
  required_initial_scans: number;
  coverage_complete: boolean;
  active_attempt: boolean;
  sources: Record<string, { first_cycle_complete: boolean; cycle_in_progress: boolean; updated_at: string | null }>;
}

export interface CafDashboard {
  bootstrap?: CafDashboardBootstrap;
  schema_version: 1;
  status: CafDashboardStatus;
  window_days: CafDashboardWindow;
  generation_id: string | null;
  generated_at: string | null;
  coverage_through: Record<string, CafDashboardSourceProgress | string | null>;
  stale: boolean;
  last_refresh_error_code: string | null;
  refresh_attempt?: { last_attempt_at: string | null; next_due_at: string | null };
  measurement: CafDashboardMeasurement | null;
  throughput: CafDashboardThroughput | null;
  retrieval: CafDashboardRetrieval | null;
  coverage: CafDashboardCoverage | null;
  precedents: CafDashboardPrecedents | null;
  clustering: CafDashboardClustering | null;
  operations: CafDashboardOperations | null;
}

export interface CafTraceCandidate {
  entry_id: string;
  version: string | number;
  score: number;
  sender: string;
  accepted: boolean;
  reason_code: string | null;
}

export interface CafSuppliedPrecedentSnapshot {
  entry_id: string;
  version: string | number;
  /** Exact immutable text that was supplied to this decision. */
  prompt_text: string;
  [key: string]: unknown;
}

export interface CafImmutableTrace {
  schema_version: 1;
  provider: string;
  model: string;
  prompt_version: string;
  policy_version: string;
  config_version: string;
  content_hash: string;
  raw_decision: Record<string, unknown>;
  effective_decision: Record<string, unknown>;
  claim_match: Record<string, unknown>;
  rule_effects: Array<Record<string, unknown>>;
  retrieval: {
    schema_version: 1;
    status: 'matched' | 'no_suitable_match' | 'technical_failure' | 'unreadable';
    attempted: boolean;
    reason_code: string | null;
    candidates: CafTraceCandidate[];
    supplied: CafSuppliedPrecedentSnapshot[];
    duration_ms: number;
    request_charge: number | null;
  };
  [key: string]: unknown;
}

export interface CafImmutableDecision {
  decision_id: string;
  attempt_id: string;
  task_id: string;
  trace: CafImmutableTrace;
  created_at: string;
}

export interface CafDecisionEvent {
  event_id: string;
  decision_id: string;
  event_key: string;
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface CafDecisionReview {
  review_id: string;
  decision_id: string;
  task_id: string;
  revision: number;
  supersedes_review_id: string | null;
  verdict: 'correct' | 'incorrect' | 'unsure';
  correction_text: string;
  source: 'staff_report' | 'random_audit' | 'targeted_audit';
  sample_assignment_id: string | null;
  actor: { id: string; email: string };
  created_at: string;
}

export interface CafDecisionReviewPage extends CursorPage<CafDecisionReview> {
  current_revision: number;
  current_review_id: string | null;
  current_by_decision: Record<string, { review_id: string; revision: number }>;
}

export interface CafDecisionReviewWrite {
  decision_id: string;
  verdict: 'correct' | 'incorrect' | 'unsure';
  correction_text: string;
  /** Ordinary staff feedback is always volunteered; sampled sources are server assigned. */
  source: 'staff_report';
  expected_revision: number;
  idempotency_key: string;
}

export interface CafCorrectionChange {
  before?: unknown;
  after?: unknown;
  [key: string]: unknown;
}

export interface CafOutboundPreviewMessage {
  effect_key: string;
  planned_effects?: string[];
  to?: Array<{ email?: string; name?: string }>;
  to_email?: string;
  to_name?: string;
  subject?: string;
  template_id?: string;
  variables?: unknown;
  personalization?: unknown;
  attachments?: Array<Record<string, unknown>>;
  template_snapshot?: {
    id?: string;
    content_hash?: string;
    provider_rendered_version_enforced?: boolean;
  };
  amendment?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface CafTaskCorrection {
  category?: string;
  action?: CafCorpusAction;
  has_final_response?: boolean;
  assigned_claim_id?: string | null;
  reopen?: boolean;
  restore_archived?: boolean;
  field_changes?: Partial<Record<
    'reasoning' | 'claim_reference' | 'client_name' | 'lender_name' |
    'agreement_number' | 'lender_reference', string | null
  >>;
  resend_intent_id?: string;
  outbound_amendment?: {
    recipient?: string;
    variables?: Record<string, string>;
    body_text?: string;
    reason: string;
  };
}

interface CafCorrectionGuidanceRequestBase {
  entry_id: string;
  sender: string;
  expected_version: number;
  reviewed_token: string | null;
  reason: string;
}

export type CafCorrectionGuidanceRequest = CafCorrectionGuidanceRequestBase & ({
  /** Strict full-document path retained for callers that hold the full entry. */
  proposed: CafCorpusEntry;
  patch?: never;
} | {
  /** Projected-list adapter: only Task 5's five editable fields are accepted. */
  patch: Partial<Pick<CafCorpusEntry,
    'correct_action' | 'note' | 'extraction_schema' |
    'archive_override_refs' | 'category_override' | 'final_response_stance'>>;
  proposed?: never;
});

export interface CafCorrectionPreviewRequest {
  decision_id: string;
  review_id: string;
  correction: CafTaskCorrection;
  guidance: CafCorrectionGuidanceRequest | null;
}

export interface CafCorrectionPreviewResponse {
  preview_id: string;
  task_id: string;
  decision_id: string;
  review_id: string;
  expires_at: string;
  task_version: string;
  changes: Record<string, CafCorrectionChange>;
  claim_changes: Array<Record<string, unknown>>;
  guidance_changes: Array<Record<string, unknown>>;
  outbound_messages: CafOutboundPreviewMessage[];
  warnings: string[];
  example_conflicts?: CafExampleConflict[];
  can_confirm: boolean;
}

export interface CafCorrectionStepResult {
  status: 'not_requested' | 'complete' | 'pending' | 'failed' | 'unknown' | string;
  effects?: string[];
  reason_code?: string;
  [key: string]: unknown;
}

export interface CafCorrectionResult {
  correction_id: string;
  repair: CafCorrectionStepResult;
  guidance: CafCorrectionStepResult & { entry_id: string | null; version: number | null };
  outbound: CafCorrectionStepResult & { intent_ids: string[] };
  warnings: string[];
}

export interface CafGuidanceVersion {
  id: string;
  entry_id: string;
  sender: string;
  document_type: 'version';
  schema_version: 1;
  /** History containers are non-current; snapshot.active carries publication state. */
  active: false;
  version: string | number;
  sequence: number;
  snapshot: CafCorpusEntry;
  amended_by: string;
  reason: string;
  recorded_at: string;
  version_provenance: 'content_snapshot' | 'logical_version' | string;
  first_captured_at?: string;
}

export interface CafDecisionTraceResponse {
  decision: CafImmutableDecision;
  events: CafDecisionEvent[];
  reviews: CafDecisionReviewPage;
}

const base = '/api/admin/caf-corpus';

const toParams = <T extends Record<string, unknown>>(q: T) => {
  const params: Record<string, unknown> = {};
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params[k] = v;
  });
  return params;
};

export const listCafClusters = (q: ClusterListQuery = {}) =>
  client
    .get<CursorPage<CafClusterListItem>>(`${base}/clusters`, {
      params: toParams({ status: q.status ?? 'unlabelled', limit: q.limit, cursor: q.cursor }),
    })
    .then((r) => r.data);

export const getCafClusterCount = (status: CafClusterStatus = 'unlabelled') =>
  client
    .get<{ count: number }>(`${base}/clusters/count`, {
      params: toParams({ status }),
    })
    .then((r) => r.data.count);

export const getCafCluster = (id: string) =>
  client.get<CafClusterDetail>(`${base}/clusters/${id}`).then((r) => r.data);

/** Paginated, content-enriched view of a cluster's FULL member list (beyond
 *  the <=5 samples `getCafCluster` returns). 404 -> axios error if the
 *  cluster no longer exists. */
export const listClusterMembers = (
  id: string,
  q: { offset?: number; limit?: number } = {}
) =>
  client
    .get<CafClusterMembersPage>(`${base}/clusters/${id}/members`, {
      params: toParams({ offset: q.offset, limit: q.limit }),
    })
    .then((r) => r.data);

/** Single-email content preview (body + attachments) for the Create Cluster
 *  picker. 404 -> axios error if the task id no longer exists. */
export const getEmailPreview = (taskId: string) =>
  client.get<CafClusterSample>(`${base}/emails/${taskId}/preview`).then((r) => r.data);

export const splitCafCluster = (id: string, payload: CafClusterSplitRequest) =>
  client.post<{ status: 'complete'; cluster: CafClusterDetail; separated_cluster: CafClusterDetail }>(
    `${base}/clusters/${id}/split`, payload,
  ).then((r) => r.data);

export const labelCafCluster = (id: string, payload: LabelClusterPayload) =>
  client
    .post<CafCorpusEntry>(`${base}/clusters/${id}/label`, payload)
    .then((r) => r.data);

export const recordCorpusFromTask = (payload: FromTaskPayload) =>
  client
    .post<CafCorpusEntry>(`${base}/from-task`, payload)
    .then((r) => r.data);

/** Save approved guidance for a task without marking the current email repaired or attaching a seed workflow. */
export const recordCorpusFromTaskGuidance = (payload: FromTaskPayload) =>
  client.post<CafCorpusEntry>(`${base}/from-task-guidance`, payload).then((r) => r.data);

/** Curator-only action for a selected lender email. This is intentionally
 * separate from corpus actions: it corrects this task's existing final-
 * response flag rather than teaching the classifier a new automatic action. */
export const markCafTaskFinalResponse = (taskId: string) =>
  client
    .post<MarkFinalResponseOutcome>(
      `${base}/tasks/${encodeURIComponent(taskId)}/actions/mark-final-response`
    )
    .then((r) => r.data);

/** Undo this action's own latest mark on one task (Task 6b). Refused with a
 *  409 (`error`, `code: CafUnmarkFinalResponseErrorCode`) for flags this
 *  action did not set -- the message is plain English for staff. */
export const unmarkCafFinalResponse = async (
  taskId: string,
  reason: string
): Promise<UnmarkFinalResponseOutcome> => (
  await client.post<UnmarkFinalResponseOutcome>(
    `${base}/tasks/${encodeURIComponent(taskId)}/actions/unmark-final-response`,
    { reason }
  )
).data;

export const listCafCorpusEntries = (q: EntryListQuery = {}) =>
  client
    .get<OffsetPage<CafCorpusEntry>>(`${base}/entries`, {
      params: toParams({ sender: q.sender, action: q.action, limit: q.limit, offset: q.offset }),
    })
    .then((r) => r.data);

/** One current entry by exact id and exact raw sender, in the list-row shape.
 *  Workflow-navigation (SQL) rows carry no note, labelled_by, version or
 *  compare_token, so Edit/Deactivate refetch the row here first. `sender` is
 *  REQUIRED -- the backend GET /entries/<id> 400s without it
 *  (admin_caf_corpus.py, caf_corpus_service.py: the row is looked up by its
 *  sender partition, not a bare cross-partition id scan). Task 24 fix 10's
 *  "open this example" jump threads a separate `exampleSender` through
 *  TeachFocus rather than calling this without one. */
export const getCafCorpusEntry = (id: string, sender: string) =>
  client
    .get<CafCorpusEntry>(`${base}/entries/${encodeURIComponent(id)}`, { params: { sender } })
    .then((r) => r.data);

export const updateCafEntry = (id: string, payload: UpdateEntryPayload) =>
  client
    .patch<CafCorpusEntry>(`${base}/entries/${id}`, payload)
    .then((r) => r.data);

export const reconcileCafProjection = (
  id: string, payload: { sender: string; expected_version: number; reason: string }
) => client.post<CafCorpusEntry>(`${base}/entries/${encodeURIComponent(id)}/reconcile-projection`, payload)
  .then((r) => r.data);

export const deleteCafCorpusEntry = (
  id: string,
  payload: Pick<UpdateEntryPayload, 'expected_version' | 'reason' | 'reviewed_token'>
) =>
  client
    // Runtime Axios supports DELETE bodies; the obsolete @types/axios copy does not.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .delete<{ deleted: boolean; id: string }>(`${base}/entries/${id}`, { data: payload } as any)
    .then((r) => r.data);

export const getCafEntryAttachments = (id: string) =>
  client.get<CafAttachment[]>(`${base}/entries/${id}/attachments`).then((r) => r.data);

/** Re-pend every AI decision that used this (corrected) precedent so the
 *  backlog processor re-decides them. Staff-touched tasks are skipped
 *  server-side. 400 (too many matches — offline script territory) and 404
 *  surface as axios errors with `response.data.error`. */
export const requeueCafEntryDecisions = (entryId: string) =>
  client
    .post<CafRequeueSummary>(`${base}/entries/${entryId}/requeue`)
    .then((r) => r.data);

/* ------------------------------------------------------------------ */
/* Teach the AI                                                        */
/* ------------------------------------------------------------------ */

export type CafEntryState = 'on' | 'off';

export interface CafEntryPage extends OffsetPage<CafCorpusEntry> {
  /** Echoed only by a backend that understands `state` (switched-off examples). */
  state?: CafEntryState;
}

/** Approved examples straight from Cosmos (full rows: note, versions, compare token). */
export const listCafEntries = async (
  q: { state: CafEntryState; sizes?: boolean; limit?: number; offset?: number },
): Promise<CafEntryPage> => (
  await client.get<CafEntryPage>(`${base}/entries`, {
    params: toParams({ state: q.state, sizes: q.sizes ? 1 : undefined, limit: q.limit, offset: q.offset }),
  })
).data;

/** An older backend ignores `state` and returns switched-on rows; only trust an echo. */
export const entriesStateSupported = (page: CafEntryPage, asked: CafEntryState) => page.state === asked;

export const reactivateCafEntry = async (
  id: string,
  payload: Pick<UpdateEntryPayload, 'expected_version' | 'reason' | 'reviewed_token'>,
): Promise<CafCorpusEntry> => (
  await client.post<CafCorpusEntry>(`${base}/entries/${encodeURIComponent(id)}/reactivate`, payload)
).data;

export interface CafRequeuePreview {
  entry_id: string;
  matched: number;
  would_requeue: number;
  skipped_staff_touched: number;
  over_cap: boolean;
}

export const previewCafEntryRequeue = async (id: string): Promise<CafRequeuePreview> => (
  await client.get<CafRequeuePreview>(`${base}/entries/${encodeURIComponent(id)}/requeue-preview`)
).data;

/** The To label queue: unlabelled groups with two sample subjects. Never navigation params. */
export const listCafClusterQueue = async (
  q: { limit?: number; cursor?: string | null } = {},
): Promise<CursorPage<CafClusterListItem>> => (
  await client.get<CursorPage<CafClusterListItem>>(`${base}/clusters`, {
    params: toParams({ status: 'unlabelled', limit: q.limit, cursor: q.cursor, with_subjects: 1 }),
  })
).data;

export const listSenderEmbeddings = (sender: string, q: SenderEmbeddingsQuery = {}) =>
  client
    .get<CursorPage<CafEmbeddingItem>>(`${base}/embeddings`, {
      params: toParams({
        sender,
        limit: q.limit,
        cursor: q.cursor,
        subject: q.subject,
        exclude_actioned: q.excludeActioned,
      }),
    })
    .then((r) => r.data);

export const createManualCluster = (payload: ManualClusterPayload) =>
  client
    .post<ManualClusterResult>(`${base}/clusters/manual`, payload)
    .then((r) => r.data);

/** The claim-field allowlist that populates the extraction-schema editor's
 *  target-field picklist. */
export const listClaimFields = () =>
  client.get<ClaimFieldOption[]>(`${base}/claim-fields`).then((r) => r.data);

/** Legacy decision-feed companion stats. New curator overview uses a stored snapshot. */
export const getCafPerformance = (days?: number) =>
  client
    .get<CafPerformanceStats>(`${base}/performance`, {
      params: toParams({ days }),
    })
    .then((r) => r.data);

/** On-demand email body for one decision (404 -> axios error if the task
 *  id no longer exists). */
export const getCafDecisionBody = (taskId: string) =>
  client
    .get<CafDecisionBody>(`${base}/decisions/${taskId}/body`)
    .then((r) => r.data);

/** Newest-first feed of AI-processed tasks with server-computed flags. */
export const listCafDecisions = async (q: DecisionListQuery = {}, signal?: AbortSignal): Promise<CafDecisionPage> => (
  await client.get<CafDecisionPage>(`${base}/decisions`, {
    params: toParams({
      days: q.days,
      action: q.action,
      // Backend treats only the literal string 'true' as flagged-only.
      flagged: q.flagged ? 'true' : undefined,
      limit: q.limit,
      cursor: q.cursor,
      enrich: q.enrich ? 1 : undefined,
    }),
    signal,
  // The obsolete @types/axios request type omits AbortSignal; runtime axios is 1.9.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any)
).data;

export const getCafReplacementReport = async (q: CafReplacementReportQuery, signal?: AbortSignal,
  acceptsAuthorization?: (authorization: string | null) => boolean): Promise<CafReplacementReport> => {
  const response = await client.get<CafReplacementReport>(`${base}/replacement-report`, {
    params: toParams({ ...q }), signal,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);
  const header = response.config?.headers?.Authorization ?? response.config?.headers?.authorization;
  if (acceptsAuthorization && !acceptsAuthorization(typeof header === 'string' ? header : null)) {
    const error = new Error('Watching report authentication changed');
    error.name = 'AbortError';
    throw error;
  }
  return response.data;
};

/** Read one already-published SQL dashboard snapshot. Never starts aggregation. */
export const getCafDashboard = async (
  days: CafDashboardWindow,
  signal?: AbortSignal,
  acceptsAuthorization?: (authorization: string | null) => boolean,
): Promise<CafDashboard> => {
  const response = await client
    // The repository still carries the obsolete @types/axios request type,
    // which omits Axios 1.x AbortSignal support. Runtime axios is 1.9.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .get<CafDashboard>(`${base}/dashboard`, { params: { days }, signal } as any);
  // Use the transport's final request config, after the shared interceptor
  // selected its token. A storage event may not have reached the provider yet.
  const header = response.config?.headers?.Authorization ?? response.config?.headers?.authorization;
  if (acceptsAuthorization && !acceptsAuthorization(typeof header === 'string' ? header : null)) {
    const error = new Error('Dashboard authentication changed');
    error.name = 'AbortError';
    throw error;
  }
  return response.data;
};

/** Exact immutable trace, append-only outcome events and review revisions. */
export const getCafDecisionTrace = async (decisionId: string, signal?: AbortSignal): Promise<CafDecisionTraceResponse> =>
  (
    await client
    .get<CafDecisionTraceResponse>(
      `${base}/decision-traces/${encodeURIComponent(decisionId)}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { signal } as any
    )
  ).data;

/** Newest-first immutable attempts for one task. */
export const listCafTaskDecisions = async (
  taskId: string,
  cursor?: string,
  signal?: AbortSignal
): Promise<CursorPage<CafImmutableDecision>> =>
  (
    await client
    .get<CursorPage<CafImmutableDecision>>(
      `${base}/tasks/${encodeURIComponent(taskId)}/decisions`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { params: toParams({ cursor }), signal } as any
    )
  ).data;

/** Append-only staff feedback for a task, optionally scoped to one decision. */
export const listCafDecisionReviews = async (
  taskId: string,
  decisionId?: string,
  cursor?: string,
  limit = 50
): Promise<CafDecisionReviewPage> => (
  await client.get<CafDecisionReviewPage>(
    `${base}/tasks/${encodeURIComponent(taskId)}/reviews`,
    { params: toParams({ decision_id: decisionId, cursor, limit }) }
  )
).data;

/** Actor, revision and time are supplied and validated by the server. */
export const recordCafDecisionReview = async (
  taskId: string,
  payload: CafDecisionReviewWrite
): Promise<CafDecisionReview> => (
  await client.post<CafDecisionReview>(
    `${base}/tasks/${encodeURIComponent(taskId)}/reviews`, payload
  )
).data;

/** Persist a harmless, actor-bound 15-minute correction preview. */
export const previewCafCorrection = async (
  taskId: string,
  payload: CafCorrectionPreviewRequest
): Promise<CafCorrectionPreviewResponse> => (
  await client.post<CafCorrectionPreviewResponse>(
    `${base}/tasks/${encodeURIComponent(taskId)}/corrections/preview`, payload
  )
).data;

/** Confirm only the server-held preview; never restate or rewrite its actions. */
export const confirmCafCorrection = async (
  taskId: string,
  payload: { preview_id: string; idempotency_key: string; confirm_outbound: boolean }
): Promise<CafCorrectionResult> => (
  await client.post<CafCorrectionResult>(
    `${base}/tasks/${encodeURIComponent(taskId)}/corrections/confirm`, payload
  )
).data;

/** Oldest-first, bounded immutable guidance history for an exact sender partition. */
export const listCafGuidanceHistory = async (
  entryId: string,
  sender: string,
  cursor?: string,
  limit = 50
): Promise<CursorPage<CafGuidanceVersion>> => (
  await client.get<CursorPage<CafGuidanceVersion>>(
    `${base}/entries/${encodeURIComponent(entryId)}/history`,
    { params: toParams({ sender, cursor, limit }) }
  )
).data;

/* ------------------------------------------------------------------ */
/* Workflow navigation (read-only coverage; grants no authority)       */
/* ------------------------------------------------------------------ */

/** Closed classifier states. `unavailable` is never "no workflow". */
export type CafWorkflowState = 'none' | 'single' | 'multiple' | 'mixed' | 'unavailable';
export type CafWorkflowFilter = 'all' | 'missing';
export type CafClusterView = 'unlabelled' | 'labelled' | 'all';

export interface CafWorkflowPin {
  workflow_id: string;
  workflow_version: number;
  /** Name from the pinned immutable version; null when the version is missing. */
  name: string | null;
  catalogue_status: 'active' | 'published' | 'missing';
  /** `/ai-curation?workflowId=…&workflowVersion=…`, or null when missing. */
  editor_url: string | null;
}

export interface CafWorkflowCell {
  state: CafWorkflowState;
  pins: CafWorkflowPin[];
  reference_count: number;
  attached_count: number;
  unattached_count: number;
  pin_count: number;
  reason_codes: string[];
  authority: 'navigation_only';
  generation: number | null;
  current: boolean;
}

/** `null` counts mean "cannot be calculated reliably yet" -- never zero. */
export interface CafNavigationCounts {
  confirmed: number | null;
  unavailable_in_scope: number | null;
  unknown_scope: number | null;
}

export interface CafNavigationInventory {
  phase: string | null;
  epoch: number | null;
  revision: number | null;
  availability: 'current' | 'unavailable';
  reason_codes: string[];
  unscoped_diagnostics: number | null;
}

export type CafNavigationItem<T> = Partial<T> & {
  id: string;
  sender: string;
  metadata: 'current' | 'last_known' | 'diagnostic' | 'unknown' | 'fallback';
  workflow: CafWorkflowCell;
};

export interface CafNavigationPage<T> {
  items: CafNavigationItem<T>[];
  transport: 'sql_keyset' | 'cosmos_offset' | 'cosmos_cursor';
  next_cursor: string | null;
  next_offset: number | null;
  counts: CafNavigationCounts;
  inventory: CafNavigationInventory;
}

export interface CafNavigationCount {
  counts: CafNavigationCounts;
  inventory: CafNavigationInventory;
  transport: string;
  /** Existing cluster total while the navigation inventory is not ready. */
  legacy_total?: number | null;
}

export interface CafNavigationStatus {
  entries: { available: boolean; reason_codes: string[] };
  clusters: { available: boolean; reason_codes: string[] };
  inventory: CafNavigationInventory;
  authority: 'navigation_only';
}

export interface CafWorkflowReference {
  entry: { sender: string; id: string };
  roles: Array<'canonical' | 'explicit'>;
  attached: boolean;
  pin: CafWorkflowPin | null;
  guidance_status: 'non_executable';
  authority: 'navigation_only';
}

export interface CafWorkflowReferencePage {
  items: CafWorkflowReference[];
  next_cursor: string | null;
  scope: 'references';
  cluster: { sender: string; id: string };
  member_count: number;
  summary: Omit<CafWorkflowCell, 'pins'>;
}

export interface CafNavigationRecoveryResult {
  operation_id: string;
  status: string;
  reason_code: string | null;
}

export interface ClusterNavigationQuery {
  view: CafClusterView;
  workflow: CafWorkflowFilter;
  limit?: number;
  cursor?: string | null;
}

export interface EntryNavigationQuery {
  workflow: CafWorkflowFilter;
  sender?: string;
  action?: CafCorpusAction;
  limit?: number;
  offset?: number;
  cursor?: string | null;
}

/**
 * Deploy-order compatibility: the frontend ships before the backend, and an
 * older backend ignores `view`/`workflow` and filters clusters only by the
 * legacy `status` parameter. So every cluster request also carries `status`:
 * the view itself when it is `unlabelled`/`labelled` (the new backend accepts
 * an agreeing status), and an explicit empty `status=` for `all` (the old list
 * treats it as "no filter" and the old count as "all" rather than its
 * unlabelled default). `toParams` drops empty strings, so it is set here.
 */
const clusterViewParams = (view: CafClusterView) => ({ status: view === 'all' ? '' : view });

export const listCafClustersNavigation = (q: ClusterNavigationQuery) =>
  client
    .get<CafNavigationPage<CafClusterListItem>>(`${base}/clusters`, {
      params: {
        ...clusterViewParams(q.view),
        ...toParams({ view: q.view, workflow: q.workflow, limit: q.limit, cursor: q.cursor }),
      },
    })
    .then((r) => r.data);

export const countCafClustersNavigation = (q: Pick<ClusterNavigationQuery, 'view' | 'workflow'>) =>
  client
    .get<CafNavigationCount>(`${base}/clusters/count`, {
      params: { ...clusterViewParams(q.view), ...toParams({ view: q.view, workflow: q.workflow }) },
    })
    .then((r) => r.data);

export const listCafEntriesNavigation = (q: EntryNavigationQuery) =>
  client
    .get<CafNavigationPage<CafCorpusEntry>>(`${base}/entries`, {
      params: toParams({
        workflow: q.workflow, sender: q.sender, action: q.action, limit: q.limit,
        offset: q.offset || undefined, cursor: q.cursor,
      }),
    })
    .then((r) => r.data);

export const countCafEntriesNavigation = (q: Pick<EntryNavigationQuery, 'workflow' | 'sender' | 'action'>) =>
  client
    .get<CafNavigationCount>(`${base}/entries/count`, {
      params: toParams({ workflow: q.workflow, sender: q.sender, action: q.action }),
    })
    .then((r) => r.data);

export const getCafNavigationStatus = () =>
  client.get<CafNavigationStatus>(`${base}/navigation/status`).then((r) => r.data);

export const listClusterWorkflowReferences = (
  clusterId: string,
  q: { sender: string; generation: number; cursor?: string | null; limit?: number },
) =>
  client
    .get<CafWorkflowReferencePage>(`${base}/clusters/${encodeURIComponent(clusterId)}/workflow-references`, {
      params: toParams({ sender: q.sender, generation: q.generation, cursor: q.cursor, limit: q.limit }),
    })
    .then((r) => r.data);

/** Body is exactly `{reason}`; the result is the private recovery outcome only. */
export const reconcileNavigationOperation = (operationId: string, reason: string) =>
  client
    .post<CafNavigationRecoveryResult>(
      `${base}/navigation-operations/${encodeURIComponent(operationId)}/reconcile`, { reason }
    )
    .then((r) => r.data);

export interface CafNavigationProblem {
  status: number | null;
  code: string | null;
  operationId: string | null;
  reasonCodes: string[];
}

/** Stable navigation refusal details from an axios error (never throws). */
export const navigationProblem = (err: unknown): CafNavigationProblem => {
  const response = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const data = response?.data || {};
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    code: typeof data.code === 'string' ? data.code : null,
    operationId: typeof data.operation_id === 'string' ? data.operation_id : null,
    reasonCodes: Array.isArray(data.reason_codes)
      ? (data.reason_codes as unknown[]).filter((c): c is string => typeof c === 'string') : [],
  };
};

export interface FinalResponsePreview {
  task_id: string;
  before: boolean | null;
  after: boolean;
  has_final_response: boolean;
  reason: string;
  expected_storage: 'active' | 'archived';
  expected_row_version: string | number;
  expected_source_hash: string;
  expected_protected_hash: string;
}
export type FinalResponseConfirmation = Pick<FinalResponsePreview,
  'has_final_response' | 'reason' | 'expected_storage' | 'expected_row_version' | 'expected_source_hash' | 'expected_protected_hash'> & { idempotency_key: string };
export const previewFinalResponse = (taskId: string, body: { has_final_response: boolean; reason: string }) =>
  client.post<FinalResponsePreview>(`${base}/tasks/${taskId}/final-response-adjudications/preview`, body).then((r) => r.data);
export const confirmFinalResponse = (taskId: string, body: FinalResponseConfirmation) =>
  client.post<{ adjudication_id: string; current: { has_final_response: boolean | null } }>(`${base}/tasks/${taskId}/final-response-adjudications/confirm`, body).then((r) => r.data);

export type CafConflictSource = { kind: 'cluster' | 'task' | 'entry'; id: string } | { kind: 'tasks'; ids: string[] };
export interface CafExampleConflictRequest {
  source: CafConflictSource;
  sender: string;
  action: CafCorpusAction;
  final_response_stance: CafFinalResponseStance;
  workflow_id?: string | null;
}
export interface CafExampleConflict {
  entry_id: string;
  subject: string;
  final_response_stance: CafFinalResponseStance | null;
  correct_action: string | null;
  workflow_id: string | null;
  workflow_name: string | null;
  similarity: number;
  reasons: Array<'opposite_setting' | 'different_action' | 'different_workflow'>;
}
export interface CafExampleConflictResult { checked: boolean; conflicts: CafExampleConflict[]; warnings: string[] }

/** Ruling R7: warn-only check of a proposed example against the sender's approved examples. */
export const checkExampleConflicts = (request: CafExampleConflictRequest) =>
  client.post<CafExampleConflictResult>(`${base}/example-conflicts`, request).then((r) => r.data);

/**
 * Final fix wave C1/C2: the feature probe behind `finalResponseSupport`. It sends
 * an empty request, which costs the server nothing: a backend that takes the
 * final-response setting has this route and refuses the request (400) before
 * any lookup, while an older backend has no such route (404 {"error": "Not Found"},
 * see `saveEngine.isMissingRoute`). Always rejects in practice; callers read the error.
 */
export const probeExampleConflicts = (): Promise<void> =>
  Promise.resolve(client.post(`${base}/example-conflicts`, {})).then(() => undefined);
